import React, { useState, useMemo, useEffect, useRef } from 'react';
import axiosInstance from '../../../api/axios';
import { isLegacyMockAudit } from './OnPageAieoAuditTab';

export function isLegacyMockSchema(schema) {
  if (!schema || typeof schema !== 'object') return false;
  if (schema.isSampleDemo === true) return false;
  const article = schema.article || schema;
  return Boolean(
    article?.headline === 'The 2026 Guide to AI Answer Engine Optimization (AIEO)' &&
    article?.mainEntityOfPage === 'https://leadhuntrix.com/blog/aieo-guide-2026'
  );
}

export const EMPTY_SCHEMA_TEMPLATES = {
  article: {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: '',
    description: '',
    mainEntityOfPage: '',
    datePublished: '',
    dateModified: '',
    author: {
      '@type': 'Organization',
      name: '',
      url: '',
    },
    publisher: {
      '@type': 'Organization',
      name: '',
      logo: {
        '@type': 'ImageObject',
        url: '',
      },
    },
  },
  faqPage: {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [],
  },
  howTo: {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: '',
    description: '',
    totalTime: '',
    estimatedCost: {
      '@type': 'MonetaryAmount',
      currency: 'USD',
      value: '',
    },
    step: [],
  },
  product: {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: '',
    description: '',
    brand: {
      '@type': 'Brand',
      name: '',
    },
    sku: '',
    offers: {
      '@type': 'Offer',
      price: '',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      url: '',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '',
      reviewCount: '',
    },
  },
};

/**
 * Validate Schema against Schema.org 2026 guidelines
 * Returns { isValid, missingRequired, warnings }
 */
export function validateSchema(schemaType, schemaObj) {
  if (!schemaObj || typeof schemaObj !== 'object') {
    return { isValid: false, missingRequired: ['Schema Object'], warnings: [] };
  }

  const missing = [];
  const warnings = [];

  if (schemaObj['@context'] !== 'https://schema.org') {
    missing.push('@context must be https://schema.org');
  }

  if (schemaType === 'article') {
    if (!schemaObj.headline || !schemaObj.headline.trim()) missing.push('Headline');
    if (!schemaObj.description || !schemaObj.description.trim()) missing.push('Description');
    if (!schemaObj.publisher?.name) warnings.push('Recommended: Publisher Name');
    if (!schemaObj.datePublished) warnings.push('Recommended: Date Published');
    if (!schemaObj.mainEntityOfPage) warnings.push('Recommended: Main Entity URL');
  } else if (schemaType === 'faqPage') {
    const list = schemaObj.mainEntity || [];
    if (!Array.isArray(list) || list.length === 0) {
      missing.push('At least 1 Question & Answer pair');
    } else {
      list.forEach((item, idx) => {
        if (!item.name || !item.name.trim()) missing.push(`Question #${idx + 1} text`);
        if (!item.acceptedAnswer?.text || !item.acceptedAnswer.text.trim()) {
          missing.push(`Answer #${idx + 1} text`);
        }
      });
    }
  } else if (schemaType === 'howTo') {
    if (!schemaObj.name || !schemaObj.name.trim()) missing.push('HowTo Title/Name');
    const steps = schemaObj.step || [];
    if (!Array.isArray(steps) || steps.length < 2) {
      missing.push('At least 2 HowTo Steps');
    } else {
      steps.forEach((step, idx) => {
        if (!step.name || !step.name.trim()) missing.push(`Step #${idx + 1} name`);
        if (!step.text || !step.text.trim()) missing.push(`Step #${idx + 1} instructions`);
      });
    }
  } else if (schemaType === 'product') {
    if (!schemaObj.name || !schemaObj.name.trim()) missing.push('Product Name');
    if (!schemaObj.offers?.price) missing.push('Offer Price');
    if (!schemaObj.offers?.priceCurrency) missing.push('Currency');
    if (!schemaObj.brand?.name) warnings.push('Recommended: Brand Name');
  }

  return {
    isValid: missing.length === 0,
    missingRequired: missing,
    warnings,
  };
}

export default function SchemaMarkupTab({
  schemaTemplates,
  briefs = [],
  pageAudit = null,
  topicClusters = [],
  onUpdateBriefContent = null,
  onOpenInStudio = null,
  initialBriefId = '',
  onAutoUpdated = null,
  onDirtyChange = null,
  autoSeedMode = null,
}) {
  const [selectedSchemaType, setSelectedSchemaType] = useState('article');
  const [isGenerating, setIsGenerating] = useState(false);
  const [statusNotification, setStatusNotification] = useState('');
  const [selectedBriefId, setSelectedBriefId] = useState(initialBriefId || '');
  const [wrapScriptTag, setWrapScriptTag] = useState(false);
  const [wrapLines, setWrapLines] = useState(false);
  const hasLoadedInitialBriefRef = useRef(false);
  // Tracks whether the user populated/edited schema since the last Auto Update.
  // Used by the modal shell to warn before closing without applying.
  const [isDirty, setIsDirty] = useState(false);

  const markDirty = () => {
    if (!isDirty) {
      setIsDirty(true);
      if (onDirtyChange) onDirtyChange(true);
    }
  };

  const markClean = () => {
    setIsDirty(false);
    if (onDirtyChange) onDirtyChange(false);
  };

  // Initialize editable templates (no mock data when no schemaTemplates or preload context)
  const [templates, setTemplates] = useState(() => {
    if (schemaTemplates && !isLegacyMockSchema(schemaTemplates)) {
      return {
        article: schemaTemplates.article || EMPTY_SCHEMA_TEMPLATES.article,
        faqPage: schemaTemplates.faqPage || EMPTY_SCHEMA_TEMPLATES.faqPage,
        howTo: schemaTemplates.howTo || EMPTY_SCHEMA_TEMPLATES.howTo,
        product: schemaTemplates.product || EMPTY_SCHEMA_TEMPLATES.product,
      };
    }
    return EMPTY_SCHEMA_TEMPLATES;
  });

  // Keep internal state updated if valid schemaTemplates changes via props
  useEffect(() => {
    if (schemaTemplates && !isLegacyMockSchema(schemaTemplates)) {
      setTemplates({
        article: schemaTemplates.article || EMPTY_SCHEMA_TEMPLATES.article,
        faqPage: schemaTemplates.faqPage || EMPTY_SCHEMA_TEMPLATES.faqPage,
        howTo: schemaTemplates.howTo || EMPTY_SCHEMA_TEMPLATES.howTo,
        product: schemaTemplates.product || EMPTY_SCHEMA_TEMPLATES.product,
      });
    }
  }, [schemaTemplates]);

  const currentSchemaObject = templates[selectedSchemaType] || {};

  // Formatted JSON string
  const currentJsonString = useMemo(() => {
    return JSON.stringify(currentSchemaObject, null, 2);
  }, [currentSchemaObject]);

  // Code to display/copy
  const displayCode = useMemo(() => {
    if (wrapScriptTag) {
      return `<script type="application/ld+json">\n${currentJsonString}\n</script>`;
    }
    return currentJsonString;
  }, [currentJsonString, wrapScriptTag]);

  // Real-time validation
  const validationResult = useMemo(() => {
    return validateSchema(selectedSchemaType, currentSchemaObject);
  }, [selectedSchemaType, currentSchemaObject]);

  const notifyUser = (msg) => {
    setStatusNotification(msg);
    setTimeout(() => setStatusNotification(''), 4000);
  };

  // Download .jsonld file handler
  const handleDownloadFile = () => {
    try {
      const blob = new Blob([currentJsonString], { type: 'application/ld+json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedSchemaType}-schema.jsonld`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      notifyUser(`Downloaded ${selectedSchemaType}-schema.jsonld`);
    } catch (e) {
      console.warn('Failed to download JSON-LD file', e);
    }
  };

  // Reset to default blank templates
  const handleResetDefaults = () => {
    setTemplates(EMPTY_SCHEMA_TEMPLATES);
    setSelectedBriefId('');
    markClean();
    notifyUser('Reset schema templates to blank baseline');
  };

  // Helper to update current schema
  const updateCurrentSchema = (updater) => {
    markDirty();
    setTemplates((prev) => {
      const current = prev[selectedSchemaType] || {};
      const updated = typeof updater === 'function' ? updater(current) : { ...current, ...updater };
      return {
        ...prev,
        [selectedSchemaType]: updated,
      };
    });
  };

  // Populate from selected Content Brief
  const handleLoadFromBrief = (briefId) => {
    setSelectedBriefId(briefId);
    if (!briefId) {
      setTemplates(EMPTY_SCHEMA_TEMPLATES);
      notifyUser('Cleared schema preload context');
      return;
    }
    const brief = briefs.find((b) => b.id === briefId);
    if (!brief) return;

    const title = brief.title || 'Enterprise Guide';
    const slug = brief.targetSlug || '/blog/article';
    const fullUrl = `https://leadhuntrix.com${slug}`;
    const desc =
      brief.conversionObjective ||
      `The comprehensive 2026 guide to ${title}, targeting ${brief.targetAudience || 'growth teams'}.`;
    const nowIso = new Date().toISOString();

    // Find linked cluster for questions
    let matchedCluster = null;
    if (brief.topicClusterId) {
      matchedCluster = topicClusters.find((c) => c.id === brief.topicClusterId);
    }
    if (!matchedCluster) {
      matchedCluster = topicClusters.find(
        (c) =>
          c.topic.toLowerCase() === title.toLowerCase() ||
          title.toLowerCase().includes(c.topic.toLowerCase())
      );
    }

    const clusterQuestions =
      matchedCluster?.relatedQuestions && matchedCluster.relatedQuestions.length > 0
        ? matchedCluster.relatedQuestions
        : [
            `How does ${title.replace(/^The \d+ Guide to /i, '')} drive measurable pipeline growth?`,
            `What are the best practices for implementing ${title.replace(/^The \d+ Guide to /i, '')}?`,
          ];

    // Extract outline sections as HowTo steps
    const outlineSteps = Array.isArray(brief.outline)
      ? brief.outline
          .filter((o) => o.level === 'H2')
          .slice(0, 4)
          .map((o, idx) => ({
            '@type': 'HowToStep',
            position: idx + 1,
            name: o.text.replace(/^\d+\.\s*/, ''),
            text: `Execute strategic best practices for ${o.text.replace(/^\d+\.\s*/, '')}.`,
            url: `${fullUrl}#step-${idx + 1}`,
          }))
      : [];

    setTemplates((prev) => ({
      ...prev,
      article: {
        ...prev.article,
        headline: title,
        description: desc,
        mainEntityOfPage: fullUrl,
        datePublished: prev.article?.datePublished || nowIso,
        dateModified: nowIso,
        author: {
          '@type': 'Organization',
          name: 'Lead Huntrix Research Team',
          url: 'https://leadhuntrix.com',
        },
      },
      faqPage: {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: clusterQuestions.slice(0, 3).map((q) => {
          const qText =
            typeof q === 'string' ? q : q?.question || q?.title || q?.name || q?.q || q?.text || '';
          const qAns =
            typeof q === 'object' && q !== null
              ? q.answer || q.a || q.directAnswerSnippet || null
              : null;
          return {
            '@type': 'Question',
            name: qText,
            acceptedAnswer: {
              '@type': 'Answer',
              text:
                qAns ||
                `Actionable, evidence-backed strategy for ${qText} tailored for enterprise B2B revenue operations.`,
            },
          };
        }),
      },
      howTo: {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: `How to Implement ${title}`,
        description: desc,
        totalTime: 'PT30M',
        estimatedCost: {
          '@type': 'MonetaryAmount',
          currency: 'USD',
          value: '0',
        },
        step:
          outlineSteps.length >= 2
            ? outlineSteps
            : [
                {
                  '@type': 'HowToStep',
                  position: 1,
                  name: 'Review Baseline Metrics & Target Entities',
                  text: 'Audit current performance and align with verified entity definitions.',
                  url: `${fullUrl}#step-1`,
                },
                {
                  '@type': 'HowToStep',
                  position: 2,
                  name: 'Execute Strategy & Embed Structured Data',
                  text: 'Deploy high-quotability definitions and embed Schema.org Article markup.',
                  url: `${fullUrl}#step-2`,
                },
              ],
      },
      product: {
        ...prev.product,
        name: `${title} Growth Package`,
        description: desc,
        offers: {
          ...prev.product?.offers,
          url: fullUrl,
        },
      },
    }));

    setSelectedBriefId(briefId);
    markDirty();
    notifyUser(`Populated schema from Content Brief: "${title}"`);
  };

  // Auto-populate when initialBriefId is supplied
  useEffect(() => {
    if (
      initialBriefId &&
      briefs.length > 0 &&
      (!hasLoadedInitialBriefRef.current || selectedBriefId !== initialBriefId)
    ) {
      handleLoadFromBrief(initialBriefId);
      hasLoadedInitialBriefRef.current = true;
    }
  }, [initialBriefId, briefs]); // eslint-disable-line react-hooks/exhaustive-deps

  // One-shot auto-seed when the Schema modal is opened via the publish flow with
  // a chosen mode ('ai' generates, 'audit' seeds from the latest audit). Manual
  // just relies on the brief preload above.
  const autoSeedHandledRef = useRef(null);
  useEffect(() => {
    if (!autoSeedMode || !initialBriefId || briefs.length === 0) return;
    const key = `${initialBriefId}:${autoSeedMode}`;
    if (autoSeedHandledRef.current === key) return;
    autoSeedHandledRef.current = key;

    if (autoSeedMode === 'audit' && pageAudit && !isLegacyMockAudit(pageAudit)) {
      handleLoadFromAudit();
    } else if (autoSeedMode === 'ai') {
      setTimeout(() => handleAiGenerate(), 0);
    }
  }, [autoSeedMode, initialBriefId, briefs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Populate from latest Page Audit
  const handleLoadFromAudit = () => {
    if (!pageAudit || isLegacyMockAudit(pageAudit)) {
      notifyUser('No active page audit found to populate from');
      return;
    }

    const title = pageAudit.title || pageAudit.targetTopic || 'Audited Page';
    const fullUrl = pageAudit.url || 'https://leadhuntrix.com/blog/article';
    const desc =
      pageAudit.aieoBlocks?.directAnswerSummary ||
      `Technical SEO and AIEO analysis for ${title} with ${pageAudit.overallScore || 85}% health score.`;
    const nowIso = new Date().toISOString();

    const faqItems =
      Array.isArray(pageAudit.questionAnswers) && pageAudit.questionAnswers.length > 0
        ? pageAudit.questionAnswers.map((qa) => {
            const qText =
              typeof qa.question === 'string'
                ? qa.question
                : qa.question?.question || qa.question?.title || qa.question?.name || qa.name || '';
            const ansText =
              (typeof qa.directAnswerSnippet === 'string' ? qa.directAnswerSnippet : null) ||
              (typeof qa.question === 'object' && qa.question?.answer
                ? qa.question.answer
                : null) ||
              (typeof qa.acceptedAnswer?.text === 'string' ? qa.acceptedAnswer.text : null) ||
              (typeof qa.recommendation === 'string' ? qa.recommendation : null) ||
              `Comprehensive answer validated for ${qText}.`;
            return {
              '@type': 'Question',
              name: qText,
              acceptedAnswer: {
                '@type': 'Answer',
                text: ansText,
              },
            };
          })
        : [
            {
              '@type': 'Question',
              name: `What is the focus of ${title}?`,
              acceptedAnswer: {
                '@type': 'Answer',
                text: desc,
              },
            },
          ];

    setTemplates((prev) => ({
      ...prev,
      article: {
        ...prev.article,
        headline: title,
        description: desc,
        mainEntityOfPage: fullUrl,
        dateModified: nowIso,
      },
      faqPage: {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqItems,
      },
      howTo: {
        ...prev.howTo,
        name: `How to Optimize: ${title}`,
        description: desc,
      },
    }));

    markDirty();
    notifyUser(`Populated schema from Page Audit: "${title}"`);
  };

  // Auto-update: inject JSON-LD into selected brief's content
  const handleAutoUpdate = async () => {
    if (!selectedBriefId) {
      notifyUser('Please select a Content Brief first');
      return;
    }

    const activeBrief = briefs.find((b) => b.id === selectedBriefId);
    if (!activeBrief) {
      notifyUser('Selected Content Brief not found');
      return;
    }

    // Generate the JSON-LD snippet with script tag
    const jsonLdSnippet = `<script type="application/ld+json">\n${currentJsonString}\n</script>`;

    // Check if content already contains schema markup
    const existingContent = activeBrief.draftArticle?.markdown || activeBrief.content || '';
    const schemaRegex = /<script type="application\/ld\+json">[\s\S]*?<\/script>/gi;

    let updatedContent;
    if (schemaRegex.test(existingContent)) {
      // Replace existing schema markup
      updatedContent = existingContent.replace(schemaRegex, jsonLdSnippet);
      notifyUser('Updated existing JSON-LD schema in Content Brief');
    } else {
      // Append new schema markup at the end
      updatedContent = existingContent ? `${existingContent}\n\n${jsonLdSnippet}` : jsonLdSnippet;
      notifyUser('Added JSON-LD schema to Content Brief');
    }

    const wordCount = updatedContent.trim().split(/\s+/).filter(Boolean).length;
    const updatedDraftArticle = {
      ...(activeBrief.draftArticle || {}),
      markdown: updatedContent,
      wordCount,
      updatedAt: new Date().toISOString(),
    };

    const updatedBrief = {
      ...activeBrief,
      content: updatedContent,
      draftArticle: updatedDraftArticle,
    };

    // Call parent callback to update the brief
    if (onUpdateBriefContent && typeof onUpdateBriefContent === 'function') {
      try {
        await onUpdateBriefContent(selectedBriefId, updatedContent);
      } catch (error) {
        console.error('[SchemaMarkupTab] Failed to update brief content:', error);
        notifyUser('Failed to update Content Brief - please try manually');
      }
    } else {
      // Fallback: make direct API call if no callback provided
      try {
        await axiosInstance.patch(`/seo/briefs/${selectedBriefId}`, {
          content: updatedContent,
        });
        notifyUser('JSON-LD schema injected into Content Brief successfully');
      } catch (error) {
        console.error('[SchemaMarkupTab] Failed to update brief via API:', error);
        notifyUser('Failed to update Content Brief - please copy manually');
      }
    }

    // Schema applied: clear the unsaved-close guard and notify the parent so the
    // modal can close and reopen Content Studio with the injected schema.
    markClean();
    if (onAutoUpdated && typeof onAutoUpdated === 'function') {
      onAutoUpdated(updatedBrief);
    } else if (onOpenInStudio && typeof onOpenInStudio === 'function') {
      // Fallback for any non-modal usage.
      onOpenInStudio(updatedBrief, { scrollToSchema: true });
    }
  };

  // Trigger AI generation via backend (with client fallback)
  const handleAiGenerate = async () => {
    const activeBrief = briefs.find((b) => b.id === selectedBriefId);
    const topicToUse =
      activeBrief?.title ||
      (!isLegacyMockAudit(pageAudit) && pageAudit?.title) ||
      currentSchemaObject.headline ||
      currentSchemaObject.name ||
      '';

    if (!topicToUse || !topicToUse.trim()) {
      notifyUser(
        'Please select a preload context (Content Brief or Audited Page) or enter a headline/name first'
      );
      return;
    }

    setIsGenerating(true);
    const urlToUse = activeBrief?.targetSlug
      ? `https://leadhuntrix.com${activeBrief.targetSlug}`
      : (!isLegacyMockAudit(pageAudit) && pageAudit?.url) ||
        currentSchemaObject.mainEntityOfPage ||
        currentSchemaObject.offers?.url ||
        '';

    try {
      const response = await axiosInstance.post('/seo/schema/generate', {
        schemaType: selectedSchemaType,
        topic: topicToUse,
        url: urlToUse,
        description: currentSchemaObject.description || '',
      });

      if (response.data && response.data.success && response.data.data) {
        setTemplates((prev) => ({
          ...prev,
          [selectedSchemaType]: response.data.data,
        }));
        markDirty();
        notifyUser(`AI generated ${selectedSchemaType} schema successfully`);
        return;
      }
    } catch (err) {
      console.warn('[SchemaMarkupTab] AI schema generation fallback:', err.message);
      // Client-side heuristic fallback
      const nowIso = new Date().toISOString();
      if (selectedSchemaType === 'article') {
        updateCurrentSchema({
          headline: topicToUse,
          description:
            currentSchemaObject.description ||
            `Actionable, evidence-backed strategy for ${topicToUse} with verified 2026 data.`,
          mainEntityOfPage: urlToUse,
          dateModified: nowIso,
        });
      } else if (selectedSchemaType === 'faqPage') {
        updateCurrentSchema({
          mainEntity: [
            {
              '@type': 'Question',
              name: `What is the core benefit of ${topicToUse}?`,
              acceptedAnswer: {
                '@type': 'Answer',
                text: `${topicToUse} streamlines multi-channel engagement, improves data fidelity, and shortens sales cycles.`,
              },
            },
            {
              '@type': 'Question',
              name: `How to implement best practices for ${topicToUse}?`,
              acceptedAnswer: {
                '@type': 'Answer',
                text: `Actionable, evidence-backed strategy for ${topicToUse} tailored for enterprise B2B revenue operations.`,
              },
            },
          ],
        });
      } else if (selectedSchemaType === 'howTo') {
        updateCurrentSchema({
          name: `How to Implement ${topicToUse}`,
          description:
            currentSchemaObject.description ||
            `Step-by-step procedural workflow for ${topicToUse}.`,
          step: [
            {
              '@type': 'HowToStep',
              position: 1,
              name: 'Review Baseline Metrics & Align Target Objectives',
              text: `Audit current performance and align strategic parameters for ${topicToUse}.`,
              url: urlToUse ? `${urlToUse}#step-1` : undefined,
            },
            {
              '@type': 'HowToStep',
              position: 2,
              name: 'Deploy Strategy & Embed Structured Data',
              text: `Execute implementation best practices and embed verified Schema.org markup for ${topicToUse}.`,
              url: urlToUse ? `${urlToUse}#step-2` : undefined,
            },
          ],
        });
      } else if (selectedSchemaType === 'product') {
        updateCurrentSchema({
          name: `${topicToUse} Solution`,
          description:
            currentSchemaObject.description ||
            `Enterprise solution and automation capabilities for ${topicToUse}.`,
          offers: {
            ...currentSchemaObject.offers,
            url: urlToUse || currentSchemaObject.offers?.url || '',
          },
        });
      }
      notifyUser(`Generated ${selectedSchemaType} schema recommendations`);
    } finally {
      setIsGenerating(false);
    }
  };

  // --- Dynamic FAQ Handlers ---
  const handleFaqAdd = () => {
    updateCurrentSchema((current) => {
      const existing = current.mainEntity || [];
      const newItem = {
        '@type': 'Question',
        name: 'New Question: How does this work?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Detailed answer explaining the solution in concise, fact-grounded terms.',
        },
      };
      return {
        ...current,
        mainEntity: [...existing, newItem],
      };
    });
  };

  const handleFaqChange = (index, field, value) => {
    updateCurrentSchema((current) => {
      const list = [...(current.mainEntity || [])];
      if (!list[index]) return current;
      if (field === 'name') {
        list[index] = { ...list[index], name: value };
      } else if (field === 'text') {
        list[index] = {
          ...list[index],
          acceptedAnswer: {
            ...list[index].acceptedAnswer,
            text: value,
          },
        };
      }
      return { ...current, mainEntity: list };
    });
  };

  const handleFaqDelete = (index) => {
    updateCurrentSchema((current) => {
      const list = (current.mainEntity || []).filter((_, idx) => idx !== index);
      return { ...current, mainEntity: list };
    });
  };

  const handleFaqMove = (index, direction) => {
    updateCurrentSchema((current) => {
      const list = [...(current.mainEntity || [])];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= list.length) return current;
      const temp = list[index];
      list[index] = list[targetIndex];
      list[targetIndex] = temp;
      return { ...current, mainEntity: list };
    });
  };

  // --- Dynamic HowTo Handlers ---
  const handleStepAdd = () => {
    updateCurrentSchema((current) => {
      const existing = current.step || [];
      const newStep = {
        '@type': 'HowToStep',
        position: existing.length + 1,
        name: `Step ${existing.length + 1}: Implementation Action`,
        text: 'Actionable description of the procedural step to be executed.',
        url: current.url ? `${current.url}#step-${existing.length + 1}` : undefined,
      };
      return {
        ...current,
        step: [...existing, newStep],
      };
    });
  };

  const handleStepChange = (index, field, value) => {
    updateCurrentSchema((current) => {
      const list = [...(current.step || [])];
      if (!list[index]) return current;
      list[index] = { ...list[index], [field]: value };
      return { ...current, step: list };
    });
  };

  const handleStepDelete = (index) => {
    updateCurrentSchema((current) => {
      const list = (current.step || [])
        .filter((_, idx) => idx !== index)
        .map((step, idx) => ({ ...step, position: idx + 1 }));
      return { ...current, step: list };
    });
  };

  const handleStepMove = (index, direction) => {
    updateCurrentSchema((current) => {
      const list = [...(current.step || [])];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= list.length) return current;
      const temp = list[index];
      list[index] = list[targetIndex];
      list[targetIndex] = temp;
      return {
        ...current,
        step: list.map((s, idx) => ({ ...s, position: idx + 1 })),
      };
    });
  };

  return (
    <div className="seo-tab-container" data-testid="schema-markup-tab">
      {/* HEADER ROW */}
      <div className="seo-tab-header-row">
        <div>
          <h2 className="seo-section-title">
            <i className="fas fa-code"></i> Structured Data &amp; Schema.org Studio
          </h2>
          <p className="seo-section-desc">
            Generate, customize, and validate JSON-LD structured data recommendations (Article,
            FAQPage, HowTo, Product) for search engine rich snippets and AI grounding.
          </p>
        </div>

        <div className="seo-tab-actions">
          <button
            type="button"
            className="seo-btn seo-btn-primary"
            onClick={handleAutoUpdate}
            disabled={!selectedBriefId}
            title={
              selectedBriefId
                ? 'Automatically inject JSON-LD into selected Content Brief and open in Content Studio'
                : 'Select a Content Brief to enable auto-update'
            }
            data-testid="schema-auto-update-btn"
          >
            <i className="fas fa-arrow-right-to-bracket"></i>
            <span>Auto Update</span>
          </button>
          <button
            type="button"
            className="seo-btn seo-btn-secondary"
            onClick={handleDownloadFile}
            title="Download .jsonld file"
            data-testid="schema-download-jsonld-btn"
          >
            <i className="fas fa-download"></i>
            <span>Download .jsonld</span>
          </button>
        </div>
      </div>

      {/* QUICK PRELOAD & SYNC TOOLBAR */}
      <div className="seo-schema-source-bar">
        <div className="seo-schema-source-left">
          <label className="seo-schema-source-label">
            <i className="fas fa-bolt text-blue"></i> Preload Context:
          </label>
          <select
            className="seo-schema-source-select"
            value={selectedBriefId}
            onChange={(e) => handleLoadFromBrief(e.target.value)}
            data-testid="schema-source-brief-select"
          >
            <option value="">-- Populate from Content Brief ({briefs.length} available) --</option>
            {briefs.map((b) => (
              <option key={b.id} value={b.id}>
                Brief: {b.title} ({b.status})
              </option>
            ))}
          </select>

          {pageAudit && !isLegacyMockAudit(pageAudit) && (
            <button
              type="button"
              className="seo-btn seo-btn-secondary seo-btn-sm"
              onClick={handleLoadFromAudit}
              title={`Load from audited page: ${pageAudit.title || pageAudit.url}`}
              data-testid="schema-load-audit-btn"
            >
              <i className="fas fa-file-shield text-purple"></i>
              <span>From Audited Page</span>
            </button>
          )}

          <button
            type="button"
            className="seo-btn seo-btn-secondary seo-btn-sm"
            onClick={handleAiGenerate}
            disabled={isGenerating}
            title="Use AI to generate tailored recommendations for current schema"
            data-testid="schema-ai-generate-btn"
          >
            <i
              className={`fas ${isGenerating ? 'fa-spinner fa-spin' : 'fa-wand-magic-sparkles text-blue'}`}
            ></i>
            <span>{isGenerating ? 'Generating...' : 'AI Generate'}</span>
          </button>

          <button
            type="button"
            className="seo-btn seo-btn-text seo-btn-sm"
            onClick={handleResetDefaults}
            title="Reset to default template"
            data-testid="schema-reset-btn"
          >
            <i className="fas fa-rotate-left"></i>
            <span>Reset</span>
          </button>
        </div>

        {statusNotification && (
          <div className="seo-schema-notification">
            <i className="fas fa-info-circle"></i> {statusNotification}
          </div>
        )}
      </div>

      {/* SCHEMA TYPE SELECTOR & VALIDATION STATUS */}
      <div className="seo-schema-selector-bar">
        <div className="seo-schema-pills">
          <button
            type="button"
            className={`seo-schema-pill-btn ${selectedSchemaType === 'article' ? 'active' : ''}`}
            onClick={() => setSelectedSchemaType('article')}
            data-testid="schema-pill-article"
          >
            <i className="fas fa-newspaper"></i>
            <span>Article Schema</span>
          </button>
          <button
            type="button"
            className={`seo-schema-pill-btn ${selectedSchemaType === 'faqPage' ? 'active' : ''}`}
            onClick={() => setSelectedSchemaType('faqPage')}
            data-testid="schema-pill-faqPage"
          >
            <i className="fas fa-circle-question"></i>
            <span>FAQPage Schema</span>
          </button>
          <button
            type="button"
            className={`seo-schema-pill-btn ${selectedSchemaType === 'howTo' ? 'active' : ''}`}
            onClick={() => setSelectedSchemaType('howTo')}
            data-testid="schema-pill-howTo"
          >
            <i className="fas fa-list-check"></i>
            <span>HowTo Schema</span>
          </button>
          <button
            type="button"
            className={`seo-schema-pill-btn ${selectedSchemaType === 'product' ? 'active' : ''}`}
            onClick={() => setSelectedSchemaType('product')}
            data-testid="schema-pill-product"
          >
            <i className="fas fa-box-archive"></i>
            <span>Product Schema</span>
          </button>
        </div>

        <div
          className={`seo-schema-status-badge ${validationResult.isValid ? 'valid' : 'warning'}`}
          data-testid="schema-status-badge"
        >
          <i
            className={`fas ${validationResult.isValid ? 'fa-check-circle' : 'fa-triangle-exclamation'}`}
          ></i>
          <span>
            {validationResult.isValid
              ? 'Valid Schema.org (2026 Spec)'
              : `Missing: ${validationResult.missingRequired.slice(0, 2).join(', ')}`}
          </span>
        </div>
      </div>

      {/* MAIN TWO-COLUMN STUDIO GRID */}
      <div className="seo-schema-editor-grid">
        {/* Left: Interactive Dynamic Field Inspector */}
        <div className="seo-schema-fields-panel">
          <div className="seo-panel-header-sub">
            <h3 className="seo-panel-title">
              <i className="fas fa-sliders text-blue"></i> Configured Schema Properties
            </h3>
            <span className="seo-tag-mini">Editable</span>
          </div>

          {/* ARTICLE SCHEMA EDITOR */}
          {selectedSchemaType === 'article' && (
            <div className="seo-field-list">
              <div className="seo-field-row">
                <div className="seo-field-group">
                  <label className="seo-field-label">Schema @type</label>
                  <select
                    className="seo-text-input"
                    value={currentSchemaObject['@type'] || 'Article'}
                    onChange={(e) => updateCurrentSchema({ '@type': e.target.value })}
                  >
                    <option value="Article">Article</option>
                    <option value="BlogPosting">BlogPosting</option>
                    <option value="NewsArticle">NewsArticle</option>
                    <option value="TechArticle">TechArticle</option>
                  </select>
                </div>
                <div className="seo-field-group">
                  <label className="seo-field-label">Main Entity URL</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    placeholder="https://leadhuntrix.com/blog/article"
                    value={currentSchemaObject.mainEntityOfPage || ''}
                    onChange={(e) => updateCurrentSchema({ mainEntityOfPage: e.target.value })}
                    data-testid="schema-article-url-input"
                  />
                </div>
              </div>

              <div className="seo-field-group">
                <label className="seo-field-label">
                  Headline <span className="text-red">*</span>
                </label>
                <input
                  type="text"
                  className="seo-text-input"
                  placeholder="Article headline or title"
                  value={currentSchemaObject.headline || ''}
                  onChange={(e) => updateCurrentSchema({ headline: e.target.value })}
                  data-testid="schema-article-headline-input"
                />
              </div>

              <div className="seo-field-group">
                <label className="seo-field-label">
                  Description <span className="text-red">*</span>
                </label>
                <textarea
                  className="seo-textarea"
                  rows={3}
                  placeholder="Concise summary for search engines and AI grounding"
                  value={currentSchemaObject.description || ''}
                  onChange={(e) => updateCurrentSchema({ description: e.target.value })}
                  data-testid="schema-article-desc-input"
                />
              </div>

              <div className="seo-field-row">
                <div className="seo-field-group">
                  <label className="seo-field-label">Publisher Name</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    placeholder="Lead Huntrix"
                    value={currentSchemaObject.publisher?.name || ''}
                    onChange={(e) =>
                      updateCurrentSchema({
                        publisher: {
                          ...currentSchemaObject.publisher,
                          '@type': 'Organization',
                          name: e.target.value,
                        },
                      })
                    }
                    data-testid="schema-article-publisher-input"
                  />
                </div>
                <div className="seo-field-group">
                  <label className="seo-field-label">Author Name</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    placeholder="Author or Team"
                    value={
                      typeof currentSchemaObject.author === 'string'
                        ? currentSchemaObject.author
                        : currentSchemaObject.author?.name || ''
                    }
                    onChange={(e) =>
                      updateCurrentSchema({
                        author: {
                          ...currentSchemaObject.author,
                          '@type': currentSchemaObject.author?.['@type'] || 'Organization',
                          name: e.target.value,
                        },
                      })
                    }
                    data-testid="schema-article-author-input"
                  />
                </div>
              </div>

              <div className="seo-field-row">
                <div className="seo-field-group">
                  <label className="seo-field-label">Date Published</label>
                  <div className="seo-input-with-button">
                    <input
                      type="text"
                      className="seo-text-input"
                      placeholder="2026-09-28T09:00:00+08:00"
                      value={currentSchemaObject.datePublished || ''}
                      onChange={(e) => updateCurrentSchema({ datePublished: e.target.value })}
                      data-testid="schema-article-date-published"
                    />
                    <button
                      type="button"
                      className="seo-btn-inline-tag"
                      onClick={() =>
                        updateCurrentSchema({ datePublished: new Date().toISOString() })
                      }
                      title="Set to current timestamp"
                    >
                      Now
                    </button>
                  </div>
                </div>
                <div className="seo-field-group">
                  <label className="seo-field-label">Date Modified</label>
                  <div className="seo-input-with-button">
                    <input
                      type="text"
                      className="seo-text-input"
                      placeholder="2026-09-30T10:15:00+08:00"
                      value={currentSchemaObject.dateModified || ''}
                      onChange={(e) => updateCurrentSchema({ dateModified: e.target.value })}
                      data-testid="schema-article-date-modified"
                    />
                    <button
                      type="button"
                      className="seo-btn-inline-tag"
                      onClick={() =>
                        updateCurrentSchema({ dateModified: new Date().toISOString() })
                      }
                      title="Set to current timestamp"
                    >
                      Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* FAQPAGE SCHEMA EDITOR */}
          {selectedSchemaType === 'faqPage' && (
            <div className="seo-faq-fields-list">
              <div className="seo-faq-header-bar">
                <span className="seo-info-text">
                  Configured{' '}
                  <strong>{(currentSchemaObject.mainEntity || []).length} FAQ items</strong>. Each
                  Question-Answer pair is formatted into machine-readable JSON-LD.
                </span>
                <button
                  type="button"
                  className="seo-btn seo-btn-primary seo-btn-sm"
                  onClick={handleFaqAdd}
                  data-testid="schema-faq-add-btn"
                >
                  <i className="fas fa-plus"></i> Add Question
                </button>
              </div>

              {(currentSchemaObject.mainEntity || []).length === 0 && (
                <div className="seo-empty-item-state">
                  <p>
                    No FAQ questions yet. Click "+ Add Question" or preload from a Content Brief
                    above.
                  </p>
                </div>
              )}

              {(currentSchemaObject.mainEntity || []).map((faq, idx) => (
                <div key={idx} className="seo-faq-item-card" data-testid={`schema-faq-card-${idx}`}>
                  <div className="seo-item-top-bar">
                    <span className="seo-badge-tag">Q{idx + 1}</span>
                    <div className="seo-item-actions">
                      <button
                        type="button"
                        className="seo-item-action-btn"
                        onClick={() => handleFaqMove(idx, -1)}
                        disabled={idx === 0}
                        title="Move Up"
                      >
                        <i className="fas fa-arrow-up"></i>
                      </button>
                      <button
                        type="button"
                        className="seo-item-action-btn"
                        onClick={() => handleFaqMove(idx, 1)}
                        disabled={idx === (currentSchemaObject.mainEntity || []).length - 1}
                        title="Move Down"
                      >
                        <i className="fas fa-arrow-down"></i>
                      </button>
                      <button
                        type="button"
                        className="seo-item-action-btn delete"
                        onClick={() => handleFaqDelete(idx)}
                        title="Delete Question"
                        data-testid={`schema-faq-delete-btn-${idx}`}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>

                  <div className="seo-field-group">
                    <label className="seo-field-label">Question Text</label>
                    <input
                      type="text"
                      className="seo-text-input"
                      placeholder="e.g. What is AI Engine Optimization?"
                      value={typeof faq.name === 'string' ? faq.name : faq.name?.question || ''}
                      onChange={(e) => handleFaqChange(idx, 'name', e.target.value)}
                      data-testid={`schema-faq-q-input-${idx}`}
                    />
                  </div>

                  <div className="seo-field-group">
                    <label className="seo-field-label">Accepted Answer Text</label>
                    <textarea
                      className="seo-textarea"
                      rows={2}
                      placeholder="Provide a direct, concise 50-word answer for rich snippet grounding"
                      value={
                        typeof faq.acceptedAnswer?.text === 'string'
                          ? faq.acceptedAnswer.text
                          : faq.acceptedAnswer?.text?.answer || ''
                      }
                      onChange={(e) => handleFaqChange(idx, 'text', e.target.value)}
                      data-testid={`schema-faq-a-input-${idx}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* HOWTO SCHEMA EDITOR */}
          {selectedSchemaType === 'howTo' && (
            <div className="seo-howto-fields-list">
              <div className="seo-field-group">
                <label className="seo-field-label">
                  HowTo Goal / Title <span className="text-red">*</span>
                </label>
                <input
                  type="text"
                  className="seo-text-input"
                  placeholder="e.g. How to Implement AIEO in 2026"
                  value={currentSchemaObject.name || ''}
                  onChange={(e) => updateCurrentSchema({ name: e.target.value })}
                  data-testid="schema-howto-name-input"
                />
              </div>

              <div className="seo-field-group">
                <label className="seo-field-label">Description</label>
                <textarea
                  className="seo-textarea"
                  rows={2}
                  placeholder="Summary of the procedural workflow"
                  value={currentSchemaObject.description || ''}
                  onChange={(e) => updateCurrentSchema({ description: e.target.value })}
                  data-testid="schema-howto-desc-input"
                />
              </div>

              <div className="seo-field-row">
                <div className="seo-field-group">
                  <label className="seo-field-label">Total Time</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    placeholder="PT25M (ISO 8601 duration)"
                    value={currentSchemaObject.totalTime || ''}
                    onChange={(e) => updateCurrentSchema({ totalTime: e.target.value })}
                  />
                </div>
                <div className="seo-field-group">
                  <label className="seo-field-label">Estimated Cost</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    placeholder="0 USD"
                    value={
                      currentSchemaObject.estimatedCost?.value !== undefined
                        ? `${currentSchemaObject.estimatedCost.value} ${currentSchemaObject.estimatedCost.currency || 'USD'}`
                        : ''
                    }
                    onChange={(e) => {
                      const parts = e.target.value.split(' ');
                      updateCurrentSchema({
                        estimatedCost: {
                          '@type': 'MonetaryAmount',
                          value: parts[0] || '0',
                          currency: parts[1] || 'USD',
                        },
                      });
                    }}
                  />
                </div>
              </div>

              <div className="seo-faq-header-bar">
                <span className="seo-info-text">
                  Configured <strong>{(currentSchemaObject.step || []).length} Steps</strong>
                </span>
                <button
                  type="button"
                  className="seo-btn seo-btn-primary seo-btn-sm"
                  onClick={handleStepAdd}
                  data-testid="schema-howto-add-btn"
                >
                  <i className="fas fa-plus"></i> Add Step
                </button>
              </div>

              {(currentSchemaObject.step || []).length === 0 && (
                <div className="seo-empty-item-state" data-testid="schema-empty-steps-state">
                  <p>
                    No HowTo steps configured yet. Click "+ Add Step" or preload from a Content
                    Brief above.
                  </p>
                </div>
              )}

              {(currentSchemaObject.step || []).map((step, idx) => (
                <div
                  key={idx}
                  className="seo-faq-item-card"
                  data-testid={`schema-howto-step-card-${idx}`}
                >
                  <div className="seo-item-top-bar">
                    <span className="seo-badge-tag green">Step {step.position || idx + 1}</span>
                    <div className="seo-item-actions">
                      <button
                        type="button"
                        className="seo-item-action-btn"
                        onClick={() => handleStepMove(idx, -1)}
                        disabled={idx === 0}
                        title="Move Up"
                      >
                        <i className="fas fa-arrow-up"></i>
                      </button>
                      <button
                        type="button"
                        className="seo-item-action-btn"
                        onClick={() => handleStepMove(idx, 1)}
                        disabled={idx === (currentSchemaObject.step || []).length - 1}
                        title="Move Down"
                      >
                        <i className="fas fa-arrow-down"></i>
                      </button>
                      <button
                        type="button"
                        className="seo-item-action-btn delete"
                        onClick={() => handleStepDelete(idx)}
                        title="Delete Step"
                        data-testid={`schema-howto-step-delete-${idx}`}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>

                  <div className="seo-field-group">
                    <label className="seo-field-label">Step Name / Heading</label>
                    <input
                      type="text"
                      className="seo-text-input"
                      placeholder="Step heading"
                      value={step.name || ''}
                      onChange={(e) => handleStepChange(idx, 'name', e.target.value)}
                      data-testid={`schema-howto-step-name-${idx}`}
                    />
                  </div>

                  <div className="seo-field-group">
                    <label className="seo-field-label">Step Instructions</label>
                    <textarea
                      className="seo-textarea"
                      rows={2}
                      placeholder="Clear instructions for executing this step"
                      value={step.text || ''}
                      onChange={(e) => handleStepChange(idx, 'text', e.target.value)}
                      data-testid={`schema-howto-step-text-${idx}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* PRODUCT SCHEMA EDITOR */}
          {selectedSchemaType === 'product' && (
            <div className="seo-field-list">
              <div className="seo-field-group">
                <label className="seo-field-label">
                  Product Name <span className="text-red">*</span>
                </label>
                <input
                  type="text"
                  className="seo-text-input"
                  placeholder="e.g. Lead Huntrix Autonomous Growth Engine"
                  value={currentSchemaObject.name || ''}
                  onChange={(e) => updateCurrentSchema({ name: e.target.value })}
                  data-testid="schema-product-name-input"
                />
              </div>

              <div className="seo-field-group">
                <label className="seo-field-label">Description</label>
                <textarea
                  className="seo-textarea"
                  rows={2}
                  placeholder="Product description and core capabilities"
                  value={currentSchemaObject.description || ''}
                  onChange={(e) => updateCurrentSchema({ description: e.target.value })}
                  data-testid="schema-product-desc-input"
                />
              </div>

              <div className="seo-field-row">
                <div className="seo-field-group">
                  <label className="seo-field-label">Brand Name</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    placeholder="Brand or Manufacturer"
                    value={currentSchemaObject.brand?.name || ''}
                    onChange={(e) =>
                      updateCurrentSchema({
                        brand: {
                          '@type': 'Brand',
                          name: e.target.value,
                        },
                      })
                    }
                    data-testid="schema-product-brand-input"
                  />
                </div>
                <div className="seo-field-group">
                  <label className="seo-field-label">SKU / Identifier</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    placeholder="LH-GROWTH-2026"
                    value={currentSchemaObject.sku || ''}
                    onChange={(e) => updateCurrentSchema({ sku: e.target.value })}
                  />
                </div>
              </div>

              <div className="seo-field-row">
                <div className="seo-field-group">
                  <label className="seo-field-label">
                    Price <span className="text-red">*</span>
                  </label>
                  <input
                    type="text"
                    className="seo-text-input"
                    placeholder="149.00"
                    value={currentSchemaObject.offers?.price || ''}
                    onChange={(e) =>
                      updateCurrentSchema({
                        offers: {
                          ...currentSchemaObject.offers,
                          '@type': 'Offer',
                          price: e.target.value,
                        },
                      })
                    }
                    data-testid="schema-product-price-input"
                  />
                </div>
                <div className="seo-field-group">
                  <label className="seo-field-label">
                    Currency <span className="text-red">*</span>
                  </label>
                  <select
                    className="seo-text-input"
                    value={currentSchemaObject.offers?.priceCurrency || 'USD'}
                    onChange={(e) =>
                      updateCurrentSchema({
                        offers: {
                          ...currentSchemaObject.offers,
                          '@type': 'Offer',
                          priceCurrency: e.target.value,
                        },
                      })
                    }
                    data-testid="schema-product-currency-input"
                  >
                    <option value="USD">USD ($)</option>
                    <option value="MYR">MYR (RM)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                    <option value="SGD">SGD (S$)</option>
                  </select>
                </div>
              </div>

              <div className="seo-field-row">
                <div className="seo-field-group">
                  <label className="seo-field-label">Availability</label>
                  <select
                    className="seo-text-input"
                    value={currentSchemaObject.offers?.availability || 'https://schema.org/InStock'}
                    onChange={(e) =>
                      updateCurrentSchema({
                        offers: {
                          ...currentSchemaObject.offers,
                          '@type': 'Offer',
                          availability: e.target.value,
                        },
                      })
                    }
                  >
                    <option value="https://schema.org/InStock">InStock</option>
                    <option value="https://schema.org/OutOfStock">OutOfStock</option>
                    <option value="https://schema.org/PreOrder">PreOrder</option>
                  </select>
                </div>
                <div className="seo-field-group">
                  <label className="seo-field-label">Offer / Pricing URL</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    placeholder="https://leadhuntrix.com/pricing"
                    value={currentSchemaObject.offers?.url || ''}
                    onChange={(e) =>
                      updateCurrentSchema({
                        offers: {
                          ...currentSchemaObject.offers,
                          '@type': 'Offer',
                          url: e.target.value,
                        },
                      })
                    }
                  />
                </div>
              </div>

              <div className="seo-field-row">
                <div className="seo-field-group">
                  <label className="seo-field-label">Rating Value (0-5)</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    placeholder="4.9"
                    value={currentSchemaObject.aggregateRating?.ratingValue || ''}
                    onChange={(e) =>
                      updateCurrentSchema({
                        aggregateRating: {
                          ...currentSchemaObject.aggregateRating,
                          '@type': 'AggregateRating',
                          ratingValue: e.target.value,
                        },
                      })
                    }
                  />
                </div>
                <div className="seo-field-group">
                  <label className="seo-field-label">Review Count</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    placeholder="184"
                    value={currentSchemaObject.aggregateRating?.reviewCount || ''}
                    onChange={(e) =>
                      updateCurrentSchema({
                        aggregateRating: {
                          ...currentSchemaObject.aggregateRating,
                          '@type': 'AggregateRating',
                          reviewCount: e.target.value,
                        },
                      })
                    }
                  />
                </div>
              </div>
            </div>
          )}

          <div className="seo-schema-note">
            <i className="fas fa-shield-halved text-purple"></i>
            <span>
              <strong>Specification Control:</strong> Generated recommendations require approval and
              validation by the site developer prior to live CMS injection.
            </span>
          </div>
        </div>

        {/* Right: Live JSON-LD Code Box */}
        <div className="seo-schema-code-panel">
          <div className="seo-code-header">
            <div className="seo-code-header-left">
              <span>JSON-LD Output (Ready for &lt;head&gt; injection)</span>
              <span className="seo-tag-mini">application/ld+json</span>
            </div>
            <div className="seo-code-header-right">
              <label className="seo-wrap-toggle-label">
                <input
                  type="checkbox"
                  checked={wrapLines}
                  onChange={(e) => setWrapLines(e.target.checked)}
                />
                <span>Wrap lines</span>
              </label>
              <label className="seo-wrap-toggle-label">
                <input
                  type="checkbox"
                  checked={wrapScriptTag}
                  onChange={(e) => setWrapScriptTag(e.target.checked)}
                />
                <span>Include &lt;script&gt; tag</span>
              </label>
            </div>
          </div>

          <pre
            className={`seo-json-code-block ${wrapLines ? 'wrap-lines' : ''}`}
            data-testid="schema-jsonld-code"
          >
            <code>{displayCode}</code>
          </pre>

          <div className="seo-code-footer-actions">
            <a
              href="https://validator.schema.org/"
              target="_blank"
              rel="noreferrer"
              className="seo-btn-text-link"
              title="Test generated structured data in Google/Schema.org validator"
            >
              <i className="fas fa-arrow-up-right-from-square"></i> Test on Schema.org Validator
            </a>
            <span className="seo-code-char-count">
              {displayCode.length} characters ({currentJsonString.split('\n').length} lines)
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
